/**
 * Chart-view sort + legend defaults.
 *
 * The Profiler results Chart view opens ranked by EBIT margin (descending) instead of
 * backend relevance order, and hides the CAGR 3Y line until the user switches it back
 * on from the legend.
 *
 * Every surface hosting the chart (search results, saved project view, team feedback
 * view, comparables tab) shares ONE sort selection across its List, Heatmap and Chart
 * views and persists it per search in localStorage. The chart default is therefore
 * applied as a read-time re-order here instead of by writing into that shared
 * selection: List and Heatmap keep relevance order and nothing new is persisted.
 * `getEffectiveChartSort` lets the sort button/menu still show which sort the chart is
 * actually using.
 */

/** Sort key the Chart view falls back to — matches the `_ebitPercentage-desc` option. */
export const CHART_DEFAULT_SORT = '_ebitPercentage-desc';

/**
 * TD-2890 — the fallback's fallback, for a set the BE served no EBIT margins for. Ranking
 * by an absent field leaves the chart in arbitrary order and lights the Sort button on an
 * entry the menu no longer offers. CAGR 3Y descending is the same substitution
 * `coerceHeatmapColorCodingForLite` already makes for the heatmap.
 */
export const CHART_DEFAULT_SORT_WITHOUT_EBIT = '_cagr3Y-desc';

/** No explicit sort chosen; the surfaces model that state as `'default'` (relevance). */
export const isRelevanceSort = (sort) => !sort || sort === 'default';

/**
 * The sort the Chart view is really ordered by. Use for UI that DISPLAYS the active
 * sort — never for applying or persisting it, which stay on the raw selection.
 */
export const getEffectiveChartSort = (selectedSort, isChartView, hasEbitData = true) => {
  if (!isChartView || !isRelevanceSort(selectedSort)) {
    return selectedSort;
  }
  return hasEbitData ? CHART_DEFAULT_SORT : CHART_DEFAULT_SORT_WITHOUT_EBIT;
};

/**
 * Should a sort option appear in the menu while the Chart view is showing?
 *
 * Relevance is a dead entry only while the chart is the thing overriding the order. Once an
 * explicit sort is picked the chart honours it, so the row has to come back — otherwise there
 * is no way to return to relevance order without leaving the view.
 */
export const isSortOptionVisibleInChart = (option, selectedSort) =>
  option?.value !== 'default' || !isRelevanceSort(selectedSort);

/**
 * EBIT margin of a company row, for ordering only. `_ebitPercentage` is the raw field
 * every sort menu sorts on. Returns null when it does not hold a usable number, which is
 * what puts the row last.
 *
 * `_heatmapEbitMargin` was a fallback here and had to go: `mapCompanyEntry` writes it
 * unconditionally as `isNaN(margin) ? 0 : margin`, so it is never null on real data and a
 * row with no EBIT figure resolved to 0 — ranking it ABOVE every loss-making peer, the
 * exact opposite of the nulls-last contract below. The two fields are not interchangeable
 * anyway: `_ebitPercentage` is the raw fraction (0.12) and `_heatmapEbitMargin` the
 * formatted percentage (12), a factor of 100 apart. Both derive from the same
 * `ebit.percentage`, so nothing is lost by reading only the raw one.
 */
const ebitMarginOf = (company) => {
  const raw = company?._ebitPercentage;

  if (raw === null || raw === undefined || raw === '') {
    return null;
  }

  const value = Number(raw);

  return Number.isNaN(value) ? null : value;
};

/** CAGR 3Y of a company row, for ordering only. Same absent-value contract as above. */
const cagr3YOf = (company) => {
  const raw = company?._cagr3Y;

  if (raw === null || raw === undefined || raw === '') {
    return null;
  }

  const value = Number(raw);

  return Number.isNaN(value) ? null : value;
};

/**
 * Order the chart's companies by EBIT margin descending — but ONLY while the user has
 * not picked a sort. An explicit selection is already applied by the surface, so the
 * array comes back untouched (same reference, so `useMemo` identity stays stable).
 *
 * Rows without a margin sort last, matching the nulls-last semantics of the surfaces'
 * descending comparators, and a genuine 0% counts as a value. `Array#sort` is stable,
 * so ties keep relevance order. `pinFirst` holds index 0 in place for the comparables
 * chart, where bar 0 is the target company and is highlighted by position.
 */
export const orderCompaniesForChartDefault = (
  companies,
  sort,
  pinFirst = false,
  hasEbitData = true
) => {
  // No default on `companies`: the optional chaining below already covers null and
  // undefined, and a default ahead of `sort`/`pinFirst` would put one before parameters
  // that have none. Anything with nothing to order comes back exactly as it arrived —
  // the one call site is handed an array by ProfilerBarChart's own `companies = []` prop.
  if (!isRelevanceSort(sort) || !companies?.length || companies.length < 2) {
    return companies;
  }

  const head = pinFirst ? companies.slice(0, 1) : [];
  const ranked = companies.slice(pinFirst ? 1 : 0);

  // Same nulls-last contract on whichever metric is ranking the bars.
  const rankBy = hasEbitData ? ebitMarginOf : cagr3YOf;

  ranked.sort((a, b) => {
    const left = rankBy(a);
    const right = rankBy(b);

    if (left === right) {
      return 0;
    }
    if (left === null) {
      return 1;
    }
    if (right === null) {
      return -1;
    }

    return right - left;
  });

  return [...head, ...ranked];
};

/**
 * Legend series selection the chart starts with. CAGR 3Y starts hidden while its legend
 * entry stays in place, so it can be switched back on. A factory rather than a shared
 * constant — the object is handed to ECharts as `legend.selected`.
 */
export const createDefaultLegendSelected = (showEbit = true) =>
  showEbit
    ? {
        Sales: true,
        EBIT: true,
        'CAGR 3Y': false,
        'EBIT Margin': true,
        'EBIT Margin (median)': true
      }
    : // Without the EBIT series, keeping CAGR 3Y hidden would open the chart on Sales bars
      // and nothing else. It becomes the line the chart is read by, so it starts visible.
      { Sales: true, 'CAGR 3Y': true };
